import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import IconButton from 'components/common/IconButton';

/**
 * ModalCrearSuperAdminAkasiaPOS
 * Modal para crear el primer usuario SuperAdmin de una licencia AkasiaPOS
 * Solo se permite cuando la licencia NO tiene ningún usuario
 */
const ModalCrearSuperAdminAkasiaPOS = ({ show, onHide, idLicencia, onCreated }) => {
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        vUsername: '',
        vEmail: '',
        vPassword: '',
        vFirstName: '',
        vLastName: ''
    });

    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Generar cadena aleatoria para usuario o contraseña
    const generarCadenaRandom = async (campo) => {
        try {
            const response = await fetch('/api/licencia/ObtenerCadenaRandom', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'text/plain'
                }
            });

            if (response.ok) {
                const data = await response.text();
                if (campo === 'vUsername') {
                    setFormData({ ...formData, vUsername: data });
                } else if (campo === 'vPassword') {
                    setFormData({ ...formData, vPassword: data });
                }
            } else {
                showErrorToast('Error al generar cadena aleatoria');
            }
        } catch (error) {
            showErrorToast('Error al generar cadena: ' + error.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (!formData.vUsername.trim()) {
            showErrorToast('El nombre de usuario es requerido');
            return;
        }
        if (!formData.vEmail.trim()) {
            showErrorToast('El correo electrónico es requerido');
            return;
        }
        if (!formData.vPassword.trim()) {
            showErrorToast('La contraseña es requerida');
            return;
        }
        if (!formData.vFirstName.trim()) {
            showErrorToast('El nombre es requerido');
            return;
        }
        if (!formData.vLastName.trim()) {
            showErrorToast('Los apellidos son requeridos');
            return;
        }

        setSaving(true);

        try {
            const payload = {
                idLicencia,
                ...formData,
                vCreatedUser: 'AKASIAPOS'
            };

            const response = await fetch('/api/UsuariosAkasiaPOS/CrearSuperAdmin', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(JSON.stringify(payload))
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error HTTP:', response.status, errorText);
                throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
            }

            const data = await response.json();

            if (data && data.iResponseType === 1) {
                showSuccessToast('Super Admin creado exitosamente');
                limpiarFormulario();
                if (onCreated) onCreated();
            } else {
                showErrorToast(data.vMessage || 'Error al crear Super Admin');
            }
        } catch (err) {
            showErrorToast('Error al crear Super Admin: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const limpiarFormulario = () => {
        setFormData({
            vUsername: '',
            vEmail: '',
            vPassword: '',
            vFirstName: '',
            vLastName: ''
        });
    };

    const handleClose = () => {
        limpiarFormulario();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="md">
            <Modal.Header closeButton>
                <Modal.Title>Crear Super Admin - Licencia AkasiaPOS</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Row>
                                    <Col>
                                        <Form.Label>Usuario <span className="text-danger">*</span></Form.Label>
                                    </Col>
                                    <Col xs="auto">
                                        <IconButton
                                            iconClassName="fs--2 me-1"
                                            variant="falcon-primary"
                                            size="sm"
                                            icon="circle"
                                            onClick={() => generarCadenaRandom('vUsername')}
                                            disabled={saving}
                                        >
                                            Generar
                                        </IconButton>
                                    </Col>
                                </Row>
                                <Form.Control
                                    type="text"
                                    name="vUsername"
                                    value={formData.vUsername}
                                    onChange={handleChange}
                                    placeholder="Ingrese nombre de usuario"
                                    required
                                    disabled={saving}
                                    autoComplete="off"
                                    onKeyUp={(event) => { event.target.value = event.target.value.toUpperCase(); }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Row>
                                    <Col>
                                        <Form.Label>Clave <span className="text-danger">*</span></Form.Label>
                                    </Col>
                                    <Col xs="auto">
                                        <IconButton
                                            iconClassName="fs--2 me-1"
                                            variant="falcon-primary"
                                            size="sm"
                                            icon="circle"
                                            onClick={() => generarCadenaRandom('vPassword')}
                                            disabled={saving}
                                        >
                                            Generar
                                        </IconButton>
                                    </Col>
                                </Row>
                                <Form.Control
                                    type="text"
                                    name="vPassword"
                                    value={formData.vPassword}
                                    onChange={handleChange}
                                    placeholder="Ingrese contraseña"
                                    required
                                    disabled={saving}
                                    autoComplete="off"
                                    onKeyUp={(event) => { event.target.value = event.target.value.toUpperCase(); }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="vFirstName"
                                    value={formData.vFirstName}
                                    onChange={handleChange}
                                    placeholder="Ingrese nombre"
                                    required
                                    disabled={saving}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Apellidos <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="vLastName"
                                    value={formData.vLastName}
                                    onChange={handleChange}
                                    placeholder="Ingrese apellidos"
                                    required
                                    disabled={saving}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Correo Electrónico <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="email"
                                    name="vEmail"
                                    value={formData.vEmail}
                                    onChange={handleChange}
                                    placeholder="correo@ejemplo.com"
                                    required
                                    disabled={saving}
                                    autoComplete="off"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        Este será el primer usuario de la licencia con permisos de Super Admin.
                        Solo se puede crear cuando no existen usuarios en la licencia.
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                    {saving ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        'Crear Super Admin'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalCrearSuperAdminAkasiaPOS.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idLicencia: PropTypes.number.isRequired,
    onCreated: PropTypes.func
};

export default ModalCrearSuperAdminAkasiaPOS;
